import { REHYDRATE } from 'redux-persist';
import { getAlpacaAccount } from 'features/invest/utils';
import { Tier } from 'features/premium/entities';
import { LOGGED_OUT } from 'actions/types';
import { formatPlural } from 'utils';
import { SavingsPlanRuleType, } from '../types';
import { sortPotProducts } from '../utils';
const POTS_ACCOUNT_OPENING_GRACE_PERIOD_MS = 60 * 60 * 1000; // 1 hour
export const initialState = {
    account: undefined,
    createPotsAccountTimestamp: undefined,
    availableToInvest: { value: 0, currency: 'GBP' },
    cashWithdrawable: { value: 0, currency: 'GBP' },
    cashUnsettled: { value: 0, currency: 'GBP' },
    pots: [],
    spacePots: {},
    deletedPots: [],
    interestPotProductsTierRateMap: {},
    hideUpdateAccountCardInPots: undefined,
    productIdWithTheHighestInterest: undefined,
    hasFunded: undefined,
    hasCreatedPot: undefined,
    hasFundedPot: undefined,
    hasCompletedOnboardingFunnel: undefined,
    savingsPlan: undefined,
    savingsPlanMode: undefined,
    savingsPlanTierFees: undefined,
    savingsPlanFrequency: undefined,
    autosaveLastPaymentCollectionDate: undefined,
};
const getSavingsPlanFromSavingsPlanResponse = (savingsPlanResponse) => {
    if (!savingsPlanResponse) {
        return undefined;
    }
    const distribution = {};
    const savingsPlanRules = {
        automated: false,
        customAmount: undefined,
        roundUpMultiplier: undefined,
    };
    savingsPlanResponse?.rules.forEach((rule) => {
        switch (rule.type) {
            case SavingsPlanRuleType.AUTOMATED: {
                savingsPlanRules.automated = true;
                break;
            }
            case SavingsPlanRuleType.ROUND_UP: {
                savingsPlanRules.roundUpMultiplier = rule.roundUpMultiplier;
                break;
            }
            case SavingsPlanRuleType.CUSTOM: {
                savingsPlanRules.customAmount = rule.amount;
                break;
            }
            default:
                break;
        }
    });
    let hasWallet = false;
    savingsPlanResponse.destinations?.forEach((destination) => {
        if (!hasWallet && destination.potId === 'WALLET') {
            hasWallet = true;
        }
        distribution[destination.potId] = destination.percentage;
    });
    const destinationLength = Object.keys(distribution).length;
    const totalPots = hasWallet ? destinationLength - 1 : destinationLength;
    const savingsPlan = {
        distribution,
        id: savingsPlanResponse.id,
        bankId: savingsPlanResponse.bankId,
        bankInfo: savingsPlanResponse.bankInfo,
        sortCode: savingsPlanResponse.sortCode,
        isPaused: savingsPlanResponse.isPaused,
        totalAllocationItems: destinationLength,
        pausedUntil: savingsPlanResponse.pausedUntil,
        accountNumber: savingsPlanResponse.accountNumber,
        allowOverdraft: savingsPlanResponse.allowOverdraft,
        transactionFee: savingsPlanResponse.transactionFee,
        mandateStatus: savingsPlanResponse.mandateStatus,
        depositStatus: savingsPlanResponse.depositStatus,
        collectionFrequency: savingsPlanResponse.collectionFrequency,
        mandateStatusDescription: savingsPlanResponse.mandateStatusDescription,
        nextPaymentCollectionDate: savingsPlanResponse.nextPaymentCollectionDate,
        allocationText: `${hasWallet ? 'Emma wallet & ' : ''}${totalPots} ${formatPlural('pot', 'pots', totalPots)}`,
        ...savingsPlanRules,
    };
    return savingsPlan;
};
const reducer = (state = initialState, action) => {
    switch (action?.type) {
        case REHYDRATE: {
            if (action.payload && action.payload.pots) {
                // Emma Wallet was removed; drop its fields from persisted state
                const { walletBalance: _walletBalance, spaceWallets: _spaceWallets, ...persistedPots } = action.payload.pots;
                return {
                    ...state, // initialize new or missing keys, so that we don’t have to declare them as optionals
                    ...persistedPots,
                };
            }
            return state;
        }
        case 'CREATE_POTS_ACCOUNT_SUCCESS': {
            return {
                ...state,
                onboarding: undefined,
            };
        }
        case 'GET_SAVINGS_PLAN_SUCCESS': {
            return {
                ...state,
                savingsPlanMode: action.payload.savingsPlanMode,
                savingsPlanTierFees: action.payload.savingsPlanTierFees,
                savingsPlanFrequency: action.payload.savingsPlanFrequency,
                autosaveLastPaymentCollectionDate: action.payload.lastPaymentCollectionDate,
                savingsPlan: getSavingsPlanFromSavingsPlanResponse(action.payload.savingsPlan),
            };
        }
        case 'POTS_ONBOARDING_FUNNEL_COMPLETED': {
            return {
                ...state,
                hasCompletedOnboardingFunnel: true,
            };
        }
        case 'GET_TRADING_ACCOUNTS_SUCCESS': {
            const { payload } = action;
            const newInfoObj = {};
            const alpacaAccount = getAlpacaAccount(payload.accounts);
            if (alpacaAccount) {
                if (alpacaAccount.account.cashWithdrawable) {
                    newInfoObj.cashWithdrawable = alpacaAccount.account.cashWithdrawable;
                }
                if (alpacaAccount.account.cashUnsettled) {
                    newInfoObj.cashUnsettled = alpacaAccount.account.cashUnsettled;
                }
                if (alpacaAccount.account.availableToInvest) {
                    newInfoObj.availableToInvest = alpacaAccount.account.availableToInvest;
                }
                if ('hasFunded' in payload && payload.hasFunded !== undefined) {
                    newInfoObj.hasFunded = alpacaAccount.hasFunded;
                }
            }
            return {
                ...state,
                ...newInfoObj,
            };
        }
        case LOGGED_OUT:
            return initialState;
        case 'DEPOSITED_TO_POT': {
            return {
                ...state,
                hasInitiatedDepositToPot: true,
            };
        }
        case 'POTS_SET_ACCOUNT_STATUS': {
            return {
                ...state,
                hasPostOpenInstructions: true,
                createPotsAccountTimestamp: Date.now(),
                account: { ...state.account, status: action.status },
            };
        }
        case 'SAVE_POTS_ONBOARDING_STATE': {
            const { automated, productId, allowOverdraft, createPotsParams, roundUpMultiplier, contributionAmount, collectionFrequency, initialDepositAmount, } = action;
            // Switching product mid-onboarding discards everything saved for the previous product
            const isProductChange = !!productId && !!state.onboarding?.productId && productId !== state.onboarding.productId;
            const previous = isProductChange ? undefined : state.onboarding;
            return {
                ...state,
                onboarding: {
                    ...previous,
                    allowOverdraft: typeof allowOverdraft !== 'undefined' ? allowOverdraft : previous?.allowOverdraft,
                    productId: productId || previous?.productId,
                    createPotsParams: createPotsParams || previous?.createPotsParams,
                    initialDepositAmount: initialDepositAmount ?? previous?.initialDepositAmount,
                    collectionFrequency: collectionFrequency || previous?.collectionFrequency,
                    automated: typeof automated !== 'undefined' ? automated : previous?.automated,
                    contributionAmount: typeof contributionAmount !== 'undefined' ? contributionAmount : previous?.contributionAmount,
                    roundUpMultiplier: typeof roundUpMultiplier !== 'undefined' ? roundUpMultiplier : previous?.roundUpMultiplier,
                },
            };
        }
        case 'CLEAR_POTS_ONBOARDING_STATE': {
            return {
                ...state,
                onboarding: undefined,
            };
        }
        case 'GET_POTS_ACCOUNT_SUCCESS': {
            const finalAccount = {
                status: state.account?.status === 'OPENING_IN_PROGRESS' &&
                    action.payload.account.status === 'NOT_STARTED' &&
                    state.createPotsAccountTimestamp !== undefined &&
                    Date.now() - state.createPotsAccountTimestamp < POTS_ACCOUNT_OPENING_GRACE_PERIOD_MS
                    ? state.account.status
                    : action.payload.account.status,
                kycVerification: action.payload.account.kycVerification,
                spaceId: action.payload.account.spaceId,
            };
            return {
                ...state,
                account: finalAccount,
                // Onboarding state only makes sense before the account is open; if it survives an ACTIVE account,
                // the create-pot/autosave screens route into the account-opening flow and the backend rejects it.
                onboarding: finalAccount.status === 'ACTIVE' ? undefined : state.onboarding,
                // A NOT_STARTED account has no pots and getPotsWorker won't refetch, so drop the cached list so stale pots aren't shown.
                ...(finalAccount.status === 'NOT_STARTED' && { pots: [], deletedPots: [] }),
                ...(action.payload.savingsPlan && {
                    savingsPlan: getSavingsPlanFromSavingsPlanResponse(action.payload.savingsPlan),
                }),
                hasFunded: action.payload.hasFunded,
                hasFundedPot: action.payload.hasFundedPot,
                hasCreatedPot: action.payload.hasCreatedPot,
                hasPostOpenInstructions: action.payload.hasPostOpenInstructions,
                hasInitiatedDepositToPot: action.payload.hasInitiatedDepositToPot,
            };
        }
        case 'CREATE_SAVINGS_PLAN_SUCCESS': {
            if (action.payload.savingsPlan) {
                const savingsPlan = getSavingsPlanFromSavingsPlanResponse(action.payload.savingsPlan);
                return {
                    ...state,
                    savingsPlan,
                };
            }
            return state;
        }
        case 'EDIT_SAVINGS_PLAN_SUCCESS': {
            if (action.extra.skipSet) {
                return state;
            }
            const savingsPlan = getSavingsPlanFromSavingsPlanResponse(action.payload.savingsPlan);
            return {
                ...state,
                savingsPlan,
            };
        }
        case 'GET_POTS_SUCCESS': {
            const sortedPots = [];
            const deletedPots = [];
            action.payload.pots.forEach((pot) => {
                if (pot.deletedAt) {
                    deletedPots.push(pot);
                }
                else if (pot.type === 'ACCUMULATED_INTEREST' || pot.type === 'ACCUMULATED_INTEREST_NOTICE') {
                    sortedPots.unshift(pot);
                }
                else {
                    sortedPots.push(pot);
                }
            });
            return {
                ...state,
                deletedPots,
                pots: sortedPots,
                cashUnsettled: action.payload.cashUnsettled,
                cashWithdrawable: action.payload.cashWithdrawable,
                availableToInvest: action.payload.availableToInvest,
            };
        }
        case 'GET_SPACE_POTS_SUCCESS': {
            const { pots } = action.payload;
            const spacePots = { ...state.spacePots };
            spacePots[action.extra.spaceId] = pots.map((pot) => ({
                ...pot,
                spaceId: action.extra.spaceId,
            }));
            return {
                ...state,
                spacePots,
            };
        }
        case 'CREATE_POT_SUCCESS': {
            return {
                ...state,
                hasCreatedPot: true,
                onboarding: undefined,
                pots: [...state.pots, action.payload.pot],
                ...(action.extra.defaultSpaceId && {
                    spacePots: {
                        ...state.spacePots,
                        [action.extra.defaultSpaceId]: [
                            ...(state.spacePots?.[action.extra.defaultSpaceId] || []),
                            action.payload.pot,
                        ],
                    },
                }),
            };
        }
        case 'UPDATE_POT_SUCCESS': {
            const newSpacePots = { ...state.spacePots };
            Object.keys(newSpacePots).forEach((spaceId) => {
                const numberSpaceId = Number(spaceId);
                newSpacePots[numberSpaceId] = newSpacePots[numberSpaceId].map((spacePot) => spacePot.id === action.payload.pot.id ? action.payload.pot : spacePot);
            });
            return {
                ...state,
                spacePots: newSpacePots,
                pots: state.pots.map((pot) => (pot.id === action.payload.pot.id ? action.payload.pot : pot)),
            };
        }
        case 'DELETE_POT_SUCCESS': {
            const { payload } = action;
            let deletedPot;
            const newSpacePots = { ...state.spacePots };
            Object.keys(newSpacePots).forEach((spaceId) => {
                const numberSpaceId = Number(spaceId);
                newSpacePots[numberSpaceId] = newSpacePots[numberSpaceId].filter((spacePot) => spacePot.id !== action.extra.potId);
            });
            const newState = {
                ...state,
                spacePots: newSpacePots,
                pots: state.pots.filter((pot) => {
                    if (pot.id === action.extra.potId) {
                        deletedPot = { ...pot, deletedAt: new Date().getTime() };
                    }
                    return pot.id !== action.extra.potId;
                }),
            };
            if (deletedPot) {
                newState.deletedPots = [...(state.deletedPots || []), deletedPot];
            }
            if (payload?.availableToInvest) {
                newState.availableToInvest = payload.availableToInvest;
            }
            if (payload?.cashWithdrawable) {
                newState.cashWithdrawable = payload.cashWithdrawable;
            }
            if (payload?.cashUnsettled) {
                newState.cashUnsettled = payload.cashUnsettled;
            }
            return newState;
        }
        case 'REOPEN_POT_SUCCESS': {
            return {
                ...state,
                pots: [...state.pots, action.payload.pot],
                deletedPots: state.deletedPots?.filter((pot) => pot.id !== action.payload.pot.id),
            };
        }
        case 'INTERNAL_TRANSFER_SUCCESS': {
            const { pots } = action.payload;
            const potMap = pots.reduce((acc, pot) => {
                acc[pot.id] = pot;
                return acc;
            }, {});
            const newSpacePots = { ...state.spacePots };
            Object.keys(newSpacePots).forEach((spaceId) => {
                const numberSpaceId = Number(spaceId);
                newSpacePots[numberSpaceId] = newSpacePots[numberSpaceId].map((spacePot) => potMap[spacePot.id] ? potMap[spacePot.id] : spacePot);
            });
            const newState = { ...state, spacePots: newSpacePots };
            newState.pots = state.pots.map((existingPot) => (potMap[existingPot.id] ? potMap[existingPot.id] : existingPot));
            return newState;
        }
        case 'WITHDRAW_POT_TO_CONNECTED_ACCOUNT_SUCCESS': {
            const newSpacePots = { ...state.spacePots };
            Object.keys(newSpacePots).forEach((spaceId) => {
                const numberSpaceId = Number(spaceId);
                newSpacePots[numberSpaceId] = newSpacePots[numberSpaceId].map((spacePot) => spacePot.id === action.payload.pot.id ? action.payload.pot : spacePot);
            });
            return {
                ...state,
                availableToInvest: {
                    ...state.availableToInvest,
                    ...action.payload.availableToInvest,
                },
                cashUnsettled: {
                    ...state.cashUnsettled,
                    ...action.payload.cashUnsettled,
                },
                cashWithdrawable: {
                    ...state.cashWithdrawable,
                    ...action.payload.cashWithdrawable,
                },
                spacePots: newSpacePots,
                pots: state.pots.map((pot) => (pot.id === action.payload.pot.id ? action.payload.pot : pot)),
            };
        }
        // add here
        case 'GET_POTS_INFORMATION_SUCCESS': {
            const potProductTierRateMap = {};
            let interestPotsTierRateMap = {};
            let productIdWithTheHighestInterest;
            let hasRecommendedPot = false;
            let hasVisibleCashIsa = false;
            const products = action.payload.products.map((potProduct) => {
                const isProductCashISA = potProduct.name.toLowerCase().includes('cash isa');
                if (potProduct.type === 'INTEREST' || potProduct.type === 'INTEREST_NOTICE') {
                    const tierRateMap = {};
                    potProduct?.rates.tierRates.forEach((rateObj) => {
                        tierRateMap[rateObj.tier] = rateObj.rate.toFixed(2);
                    });
                    const currentProductUltimateRate = Number(tierRateMap[Tier.ultimate]);
                    const currentHighestUltimateRate = Number(interestPotsTierRateMap?.[Tier.ultimate]);
                    /**
                     * the following is the calculation to find the pot product with the highest interest rate
                     */
                    if (potProduct.isVisible) {
                        if (isProductCashISA && potProduct.isRecommended) {
                            hasVisibleCashIsa = true;
                        }
                        if (potProduct.isRecommended && !isProductCashISA) {
                            if (!hasRecommendedPot ||
                                !currentHighestUltimateRate ||
                                (currentProductUltimateRate && currentProductUltimateRate > currentHighestUltimateRate)) {
                                interestPotsTierRateMap = tierRateMap;
                                productIdWithTheHighestInterest = potProduct.id;
                            }
                            hasRecommendedPot = true;
                        }
                        else if (!hasRecommendedPot) {
                            if (!currentHighestUltimateRate ||
                                (currentProductUltimateRate && currentProductUltimateRate > currentHighestUltimateRate)) {
                                interestPotsTierRateMap = tierRateMap;
                                productIdWithTheHighestInterest = potProduct.id;
                            }
                        }
                    }
                    /**
                     * we have to update the rateMap with pot products that are not visible for backwards compatibility
                     * the users might have pots with the potProduct that is not visible now
                     */
                    potProductTierRateMap[potProduct.id] = tierRateMap;
                }
                return {
                    ...potProduct,
                    ...(isProductCashISA && { isProductCashISA: true }),
                };
            });
            sortPotProducts(products, potProductTierRateMap);
            return {
                ...state,
                products,
                hasVisibleCashIsa,
                interestPotsTierRateMap,
                productIdWithTheHighestInterest,
                interestPotProductsTierRateMap: potProductTierRateMap,
            };
        }
        case 'HIDE_UPDATE_ACCOUNT_CARD_IN_POTS':
            return {
                ...state,
                hideUpdateAccountCardInPots: true,
            };
        default:
            return state;
    }
};
export default reducer;
